import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, Lock, User, Loader2 } from 'lucide-react'
import { assets } from '../config/assets'
import { register } from '../api/auth'
import { useAuthStore } from '../store/authStore'
import { trackEvent } from '../utils/metaPixel'
import UiButton from '../components/ui/UiButton'

export default function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setUser = useAuthStore((state) => state.setUser)

  const returnUrl = searchParams.get('returnUrl') || '/moja-biblioteka'

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!firstName || !lastName || !email || !password) {
      setError(t('register.allFieldsRequired', 'Wszystkie pola są wymagane'))
      return
    }

    if (password.length < 6) {
      setError(t('register.passwordTooShort', 'Hasło musi mieć co najmniej 6 znaków'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('register.passwordsDoNotMatch', 'Hasła nie są identyczne'))
      return
    }

    if (!termsAccepted) {
      setError(t('register.termsRequired', 'Akceptacja regulaminu i polityki prywatności jest wymagana'))
      return
    }

    setIsLoading(true)

    try {
      const user = await register({
        email,
        password,
        firstName,
        lastName,
        acceptsTerms: termsAccepted,
        marketingConsent,
      })
      setUser(user)
      trackEvent('CompleteRegistration')
      setSuccess(true)
      // Brief pause so the user sees the success message before redirecting.
      setTimeout(() => {
        navigate(returnUrl)
      }, 1500)
    } catch (err) {
      setError(err.message || t('register.genericError', 'Wystąpił błąd. Spróbuj ponownie.'))
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-brand-blue px-4 py-12">
      <div
        className="absolute inset-0 bg-cover bg-fixed bg-center pointer-events-none"
        style={{ backgroundImage: `url(${assets.backgroundPattern})` }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          className="block text-center font-climate text-brand-gold text-3xl md:text-4xl mb-8 hover:text-brand-orange transition"
        >
          Abonamenty Literackie
        </Link>

        <div className="bg-white/25 backdrop-blur-md rounded-[2rem] border border-white/10 shadow-xl p-8 md:p-10">
          <h1 className="font-climate text-brand-blue text-3xl mb-2 text-center">
            {t('register.title', 'Załóż konto')}
          </h1>
          <p className="text-brand-blue/80 text-center mb-8">
            {t('register.subtitle', 'Dołącz do biblioteki')}
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-red-100/80 text-red-900 text-sm font-medium">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-2xl bg-green-100/80 text-green-900 text-sm font-medium">
              {t('register.success', 'Konto zostało utworzone. Zaraz Cię przekierujemy...')}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-bold text-brand-blue mb-2">
                  {t('register.firstName', 'Imię')}
                </label>
                <div className="relative">
                  <User
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                    aria-hidden="true"
                  />
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={isLoading || success}
                    autoComplete="given-name"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                    placeholder={t('register.firstNamePlaceholder', 'Jan')}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-bold text-brand-blue mb-2">
                  {t('register.lastName', 'Nazwisko')}
                </label>
                <div className="relative">
                  <User
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                    aria-hidden="true"
                  />
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={isLoading || success}
                    autoComplete="family-name"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                    placeholder={t('register.lastNamePlaceholder', 'Kowalski')}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-brand-blue mb-2">
                {t('register.email', 'E-mail')}
              </label>
              <div className="relative">
                <Mail
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                  aria-hidden="true"
                />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading || success}
                    autoComplete="email"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                    placeholder={t('register.emailPlaceholder', 'twoj@email.pl')}
                  />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-brand-blue mb-2">
                {t('register.password', 'Hasło')}
              </label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                  aria-hidden="true"
                />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading || success}
                    autoComplete="new-password"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                    placeholder={t('register.passwordPlaceholder', '••••••••')}
                  />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-bold text-brand-blue mb-2">
                {t('register.confirmPassword', 'Powtórz hasło')}
              </label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                  aria-hidden="true"
                />
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading || success}
                    autoComplete="new-password"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                    placeholder={t('register.confirmPasswordPlaceholder', '••••••••')}
                  />
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 text-sm text-brand-blue cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={isLoading || success}
                  required
                  className="mt-1 h-4 w-4 accent-brand-gold cursor-pointer disabled:opacity-60"
                />
                <span>
                  {t('register.termsLabel', 'Akceptuję')}{' '}
                  <Link to="/regulamin" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-brand-orange">
                    {t('register.termsLinkRegulamin', 'Regulamin')}
                  </Link>{' '}
                  {t('register.termsAnd', 'i')}{' '}
                  <Link to="/rodo" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-brand-orange">
                    {t('register.termsLinkPrivacy', 'Politykę prywatności')}
                  </Link>
                  <span className="text-brand-orange ml-1" aria-hidden="true">*</span>
                </span>
              </label>

              <label className="flex items-start gap-3 text-sm text-brand-blue cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  disabled={isLoading || success}
                  className="mt-1 h-4 w-4 accent-brand-gold cursor-pointer disabled:opacity-60"
                />
                <span>
                  {t('register.marketingLabel', 'Zgadzam się na otrzymywanie newslettera i informacji o nowościach w Abonamentach Literackich')}
                </span>
              </label>
            </div>

            <UiButton
              type="submit"
              variant="gold"
              size="lg"
              fullWidth
              disabled={isLoading || success || !termsAccepted}
              icon={isLoading ? Loader2 : undefined}
            >
              {success
                ? t('register.successButton', 'Przekierowujemy...')
                : isLoading
                  ? t('register.loading', 'Tworzenie konta...')
                  : t('register.submit', 'Załóż konto')}
            </UiButton>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="opacity-70">{t('register.haveAccount', 'Masz już konto?')}</span>
            <Link
              to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
              className="text-brand-blue hover:text-brand-orange font-bold transition ml-1"
            >
              {t('register.login', 'Zaloguj się')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
